// Que planes ofrece la pantalla de cambio de plan, y cual viene marcado.
//
// La grilla es COMPLETA (2026-08-24): el plan vigente del usuario tambien es
// una card, marcada con la chapita "Plan actual". Antes se lo sacaba de la
// lista y vivia en un cartel aparte arriba; el cartel desaparecio, asi que el
// unico lugar donde el usuario ve que tiene hoy es su propia card.
//
// Cual viene marcada, por orden de prioridad:
//   a. el `plan_id` que pidio la OTT en la URL (deep link explicito, manda);
//   b. el plan que el usuario YA tiene — el default es "no cambiar nada", y el
//      boton de la pantalla arranca diciendo "Conservar plan";
//   c. el plan recomendado que configuro el partner en el backoffice;
//   d. el de mayor precio, cuando no hay ni vigente ni recomendado.
//
// Puro y sin React a proposito: la logica de que se ofrece y que se marca es lo
// unico que hay que poder testear de esta pantalla.

import type { Plan } from '@/lib/api';

export interface PlanOptionsInput {
  /** Planes activos del partner (el orden de entrada se preserva). */
  plans: Plan[];
  /** Plan vigente del usuario. Sigue en la grilla, marcado. null = sin plan. */
  currentPlanId?: string | null;
  /** `?plan_id=` de la OTT. Gana sobre el recomendado si sigue disponible. */
  preferredPlanId?: string | null;
}

export interface PlanOptions {
  /** Lo que se muestra como cards, en el orden recibido. */
  options: Plan[];
  /**
   * Que viene marcado. `null` solo cuando el partner no tiene ningun plan
   * activo — el caller muestra el vacio.
   */
  defaultPlanId: string | null;
}

/** El mas caro. Empate: el primero, para que el resultado sea estable. */
function highestPriced(plans: Plan[]): Plan | null {
  return plans.reduce<Plan | null>(
    (best, p) => (best === null || p.price_amount > best.price_amount ? p : best),
    null,
  );
}

export function planOptionsFor({
  plans,
  currentPlanId,
  preferredPlanId,
}: PlanOptionsInput): PlanOptions {
  const options = [...plans];

  const preferred = preferredPlanId
    ? options.find((p) => p.plan_id === preferredPlanId)
    : undefined;
  if (preferred) return { options, defaultPlanId: preferred.plan_id };

  // El vigente gana sobre el recomendado: quien entra a mirar sus planes no
  // tiene que encontrarse un upgrade preseleccionado que no pidio.
  const current = currentPlanId
    ? options.find((p) => p.plan_id === currentPlanId)
    : undefined;
  if (current) return { options, defaultPlanId: current.plan_id };

  const recommended = options.find((p) => p.recommended === true);
  if (recommended) return { options, defaultPlanId: recommended.plan_id };

  return { options, defaultPlanId: highestPriced(options)?.plan_id ?? null };
}

// ---------------------------------------------------------------------------
// Entrada directa al checkout (`?checkout_plan_id=`)
// ---------------------------------------------------------------------------
//
// La landing del partner tiene dos clases de boton hacia esta pantalla:
//
//   "Gestionar mi plan"        → el usuario todavia no eligio  → grilla
//   "Quiero el plan Full"      → el usuario YA eligio           → checkout
//
// El segundo caso manda `checkout_plan_id`. Cuando apunta a un plan usable, la
// pantalla saltea la grilla y arranca en el paso de pago, que ya dice que plan
// se esta adquiriendo. Cuando NO es usable, no es un error: se cae a la grilla
// de siempre. La landing puede tener un boton apuntando a un plan que se
// archivo, o que el usuario ya tiene; en ninguno de esos casos queremos una
// pantalla de error — queremos que elija.

export type DirectCheckoutRejection =
  /** Sin `checkout_plan_id`: el flujo normal, ni siquiera se pidio. */
  | 'not_requested'
  /** El id no esta entre los planes activos del partner (typo, o plan archivado). */
  | 'unknown_plan'
  /** Plan gratuito: no hay nada que cobrar, asi que no hay pantalla de pago. */
  | 'free_plan'
  /** Ya lo tiene vigente: "cambiar" al mismo plan no es una compra. */
  | 'already_active';

export type DirectCheckout =
  | { ok: true; plan: Plan }
  | { ok: false; reason: DirectCheckoutRejection };

export interface DirectCheckoutInput {
  /** Planes activos del partner. */
  plans: Plan[];
  /** El `?checkout_plan_id=` de la URL. */
  checkoutPlanId?: string | null;
  /** Plan VIGENTE del usuario (null si no tiene o si no esta activo). */
  currentPlanId?: string | null;
}

export function directCheckoutPlan({
  plans,
  checkoutPlanId,
  currentPlanId,
}: DirectCheckoutInput): DirectCheckout {
  if (!checkoutPlanId) return { ok: false, reason: 'not_requested' };

  const plan = plans.find((p) => p.plan_id === checkoutPlanId);
  if (!plan) return { ok: false, reason: 'unknown_plan' };
  if (plan.price_amount <= 0) return { ok: false, reason: 'free_plan' };
  if (currentPlanId && plan.plan_id === currentPlanId) {
    return { ok: false, reason: 'already_active' };
  }
  return { ok: true, plan };
}

/** Aviso en consola para el integrador. Silencioso en el flujo normal. */
export function directCheckoutWarning(
  reason: DirectCheckoutRejection,
  checkoutPlanId: string,
): string | null {
  switch (reason) {
    case 'not_requested':
      return null;
    case 'unknown_plan':
      return `checkout_plan_id "${checkoutPlanId}" no es un plan activo de este partner: se muestra el selector de planes.`;
    case 'free_plan':
      return `checkout_plan_id "${checkoutPlanId}" es un plan gratuito y no tiene pantalla de pago: se muestra el selector de planes.`;
    case 'already_active':
      return `checkout_plan_id "${checkoutPlanId}" ya es el plan vigente del usuario: se muestra el selector de planes.`;
  }
}
